var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import UITypes, { checkboxIconList, durationOptions, isLinksOrLTAR, ratingIconList, } from '../../lib/UITypes';
import { LongTextAiMetaProp, VIEW_GRID_DEFAULT_WIDTH } from '../../lib/globals';
const convertToSnakeCase = (str) => {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};
const convertToCamelCase = (str) => {
    return str.replace(/_([a-z])/g, (_, letter) => `${letter.toUpperCase()}`);
};
const columnsWithOptions = [
    UITypes.Lookup,
    UITypes.Links,
    UITypes.LinkToAnotherRecord,
    UITypes.Rollup,
    UITypes.Lookup,
    UITypes.Barcode,
    UITypes.Formula,
    UITypes.QrCode,
    UITypes.Button,
    UITypes.LongText,
];
export class ApiV3DataTransformationBuilder {
    constructor() {
        this.transformations = [];
    }
    remapColumns(mappings) {
        this.transformations.push((data) => {
            return Object.entries(data).reduce((result, [key, value]) => {
                const newKey = mappings[key] || key;
                result[newKey] = value;
                return result;
            }, {});
        });
        return this;
    }
    filterColumns(args) {
        this.transformations.push((data) => {
            return Object.keys(data)
                .filter((key) => {
                if ('allowed' in args)
                    return args.allowed.includes(key);
                if ('excluded' in args)
                    return !args.excluded.includes(key);
                return true;
            })
                .reduce((result, key) => {
                result[key] = data[key];
                return result;
            }, {});
        });
        return this;
    }
    metaTransform(_a = {}) {
        var { snakeCase = true, camelCase = false, mappings = {}, metaProps = ['meta'], skipTransformFor, skipfn } = _a, rest = __rest(_a, ["snakeCase", "camelCase", "mappings", "metaProps", "skipTransformFor", "skipfn"]);
        this.transformations.push((data) => {
            const result = Object.assign({}, data);
            // iterate and update properties of metaProps only
            for (const prop of metaProps) {
                if (result[prop]) {
                    if (typeof result[prop] === 'string') {
                        try {
                            result[prop] = JSON.parse(result[prop]);
                        }
                        catch (_a) { }
                    }
                    else {
                        result[prop] = Object.assign({}, result[prop]);
                    }
                    result[prop] = Object.entries(result[prop])
                        .filter(([key]) => {
                        if ('excluded' in rest) {
                            return !rest.excluded.includes(key);
                        }
                        if ('allowed' in rest) {
                            return rest.allowed.includes(key);
                        }
                        return true;
                    })
                        .reduce((result, [key, value]) => {
                        let newKey = mappings[key] || key;
                        if ((skipTransformFor && skipTransformFor.includes(newKey)) ||
                            (skipfn && skipfn(data))) {
                            result[newKey] = value;
                            return result;
                        }
                        if (camelCase) {
                            newKey = convertToCamelCase(newKey);
                        }
                        else if (snakeCase) {
                            newKey = convertToSnakeCase(newKey);
                        }
                        result[newKey] = value;
                        return result;
                    }, {});
                }
            }
            return result;
        });
        return this;
    }
    customTransform(transformFn) {
        this.transformations.push(transformFn);
        return this;
    }
    build(data) {
        if (Array.isArray(data)) {
            return data.map((item) => this.transformations.reduce((result, transform) => transform(result), item));
        }
        return this.transformations.reduce((result, transform) => transform(result), data);
    }
    excludeNulls() {
        this.transformations.push((data) => {
            return Object.entries(data).reduce((result, [key, value]) => {
                if (value !== null) {
                    result[key] = value;
                }
                return result;
            }, {});
        });
        return this;
    }
    excludeEmptyObject() {
        this.transformations.push((data) => {
            return Object.entries(data).reduce((result, [key, value]) => {
                if (typeof value !== 'object' || Object.keys(value !== null && value !== void 0 ? value : {}).length > 0) {
                    result[key] = value;
                }
                return result;
            }, {});
        });
        return this;
    }
    transformToBoolean(booleanProps) {
        this.transformations.push((data) => {
            return Object.entries(data).reduce((result, [key, value]) => {
                if (booleanProps.includes(key)) {
                    result[key] = !!value;
                }
                else {
                    result[key] = value;
                }
                return result;
            }, {});
        });
        return this;
    }
    nestedExtract(nestedExtract) {
        this.transformations.push((data) => {
            const result = Object.assign({}, data);
            Object.entries(nestedExtract).forEach(([key, path]) => {
                const value = path.reduce((acc, key) => acc === null || acc === void 0 ? void 0 : acc[key], result);
                result[key] = value;
            });
            return result;
        });
    }
    orderProps(order) {
        // order props by order column and missing one keep at the end
        this.transformations.push((data) => {
            // Initialize the ordered object with properties based on the order array
            const ordered = order.reduce((acc, key) => {
                if (key in data) {
                    acc[key] = data[key];
                }
                return acc;
            }, {});
            // Add remaining properties from data that are not in the order array
            Object.keys(data).forEach((key) => {
                if (!order.includes(key)) {
                    ordered[key] = data[key];
                }
            });
            return ordered;
        });
        return this;
    }
}
export const builderGenerator = (_a) => {
    var { mappings, transformFn, meta, excludeNullProps = true, excludeEmptyObjectProps = false, booleanProps, nestedExtract } = _a, rest = __rest(_a, ["mappings", "transformFn", "meta", "excludeNullProps", "excludeEmptyObjectProps", "booleanProps", "nestedExtract"]);
    return () => {
        const builder = new ApiV3DataTransformationBuilder();
        if (nestedExtract) {
            builder.nestedExtract(nestedExtract);
        }
        if (excludeNullProps) {
            builder.excludeNulls();
        }
        if (excludeEmptyObjectProps) {
            builder.excludeEmptyObject();
        }
        if (booleanProps) {
            builder.transformToBoolean(booleanProps);
        }
        if ('allowed' in rest || 'excluded' in rest) {
            builder.filterColumns(rest);
        }
        if ('orderProps' in rest) {
            builder.orderProps(rest.orderProps);
        }
        if (meta) {
            builder.metaTransform(meta);
        }
        if (mappings) {
            builder.remapColumns(mappings);
        }
        if (transformFn) {
            builder.customTransform(transformFn);
        }
        return builder;
    };
};
export const colOptionBuilder = builderGenerator({
    allowed: [
        'formula_raw',
        'fk_qr_value_column_id',
        'fk_barcode_value_column_id',
        'fk_related_model_id',
        'type',
        'fk_relation_column_id',
        'fk_rollup_column_id',
        'fk_lookup_column_id',
        'rollup_function',
        'fk_webhook_id',
    ],
    mappings: {
        formula_raw: 'formula',
        fk_qr_value_column_id: 'qrcode_value_field_id',
        fk_barcode_value_column_id: 'barcode_value_field_id',
        fk_related_model_id: 'related_table_id',
        fk_relation_column_id: 'related_field_id',
        fk_rollup_column_id: 'related_table_rollup_field_id',
        fk_lookup_column_id: 'related_table_lookup_field_id',
        related_table_rollup_field_id: 'fk_rollup_column_id',
        fk_webhook_id: 'button_hook_id',
        fk_script_id: 'script_id',
    },
});
export const columnBuilder = builderGenerator({
    allowed: [
        'id',
        'title',
        'uidt',
        'cdf',
        'description',
        'meta',
        'colOptions',
        'fk_model_id',
        'system',
        'unique',
    ],
    mappings: {
        uidt: 'type',
        cdf: 'default_value',
        meta: 'options',
        fk_model_id: 'table_id',
    },
    excludeNullProps: true,
    booleanProps: ['unique'], // Ensure unique is always included even if false
    meta: {
        snakeCase: true,
        metaProps: ['meta'],
        mappings: {
            is12hrFormat: '12hr_format',
            separator: 'separator',
            richMode: 'rich_text',
            [LongTextAiMetaProp]: 'generate_text_using_ai',
            isDisplayTimezone: 'display_timezone',
            is_multi: 'allow_multiple_users',
            is_progress: 'show_as_progress',
            max: 'max_value',
        },
        excluded: [
            'defaultViewColOrder',
            'defaultViewColVisibility',
            'singular',
            'plural',
        ],
        skipTransformFor: [
            'currency_locale',
            'currency_code',
            'icon',
            'iconIdx',
            'duration',
        ],
    },
    transformFn: (data) => {
        var _a, _b, _c, _d, _e, _f;
        let options = data.options || {};
        if (data.colOptions) {
            switch (data.type) {
                case UITypes.SingleSelect:
                case UITypes.MultiSelect:
                    {
                        const choices = data.colOptions.options.map((opt) => {
                            const res = {
                                title: opt.title,
                                color: opt.color,
                            };
                            if (opt.id)
                                res.id = opt.id;
                            return res;
                        });
                        options.choices = choices;
                    }
                    break;
                default:
                    {
                        const additionalOptions = colOptionBuilder().build(data.colOptions) || {};
                        Object.assign(options, additionalOptions);
                    }
                    break;
            }
        }
        if (data.type === UITypes.Checkbox) {
            const { icon, iconIdx } = options, rest = __rest(options, ["icon", "iconIdx"]);
            // extract option meta and include only label and color
            options = rest;
            if (iconIdx) {
                options.icon = (_a = checkboxIconList[iconIdx]) === null || _a === void 0 ? void 0 : _a.label;
            }
            else if (icon) {
                options.icon = (_b = checkboxIconList.find((ic) => ic.checked === (icon === null || icon === void 0 ? void 0 : icon['checked']))) === null || _b === void 0 ? void 0 : _b.label;
            }
        }
        else if (data.type === UITypes.Rating) {
            const { icon, iconIdx } = options, rest = __rest(options, ["icon", "iconIdx"]);
            // extract option meta and include only label and color
            options = rest;
            if (iconIdx !== undefined && iconIdx !== null) {
                options.icon = (_c = ratingIconList[iconIdx]) === null || _c === void 0 ? void 0 : _c.label;
            }
            else if (icon) {
                options.icon = (_d = ratingIconList.find((ic) => ic.full === (icon === null || icon === void 0 ? void 0 : icon['full']))) === null || _d === void 0 ? void 0 : _d.label;
            }
        }
        else if (data.type === UITypes.Duration) {
            const { duration, duration_format } = options, rest = __rest(options, ["duration", "duration_format"]);
            const durationFormat = duration !== null && duration !== void 0 ? duration : duration_format;
            // extract option meta and include only label and color
            options = rest;
            if (durationFormat !== undefined && durationFormat !== null) {
                options.duration_format = (_e = durationOptions[durationFormat]) === null || _e === void 0 ? void 0 : _e.title;
            }
        }
        else if (data.type === UITypes.Button) {
            const { type } = options, rest = __rest(options, ["type"]);
            // Transform button properties based on type
            if (type === 'formula') {
                options = {
                    type,
                    formula: rest.formula,
                    label: rest.label,
                    color: rest.color,
                    theme: rest.theme,
                    icon: rest.icon,
                };
            }
            else if (type === 'webhook') {
                options = {
                    type,
                    webhook_id: rest.button_hook_id,
                    label: rest.label,
                    color: rest.color,
                    theme: rest.theme,
                    icon: rest.icon,
                };
            }
            else if (type === 'script') {
                options = {
                    type,
                    script_id: rest.script_id,
                    label: rest.label,
                    color: rest.color,
                    theme: rest.theme,
                    icon: rest.icon,
                };
            }
            else if (type === 'ai') {
                options = {
                    type,
                    prompt: rest.prompt,
                    integration_id: rest.integration_id,
                    output_column_ids: rest.output_column_ids,
                    label: rest.label,
                    color: rest.color,
                    theme: rest.theme,
                    icon: rest.icon,
                };
            }
            else {
                // Fallback to original transformation
                options = Object.assign(Object.assign({}, rest), { button_type: type });
            }
        }
        else if (isLinksOrLTAR(data.type)) {
            const { type } = options, rest = __rest(options, ["type"]);
            options = Object.assign(Object.assign({}, rest), { relation_type: type });
        }
        // exclude rollup function if Links
        if (data.type === UITypes.Links && options && options.rollup_function) {
            options.rollup_function = undefined;
        }
        return Object.assign(Object.assign({}, data), { colOptions: undefined, options: options && ((_f = Object.keys(options)) === null || _f === void 0 ? void 0 : _f.length) ? options : undefined, 
            // Explicitly preserve unique property
            unique: data.unique });
    },
});
export const columnOptionsV3ToV2Builder = builderGenerator({
    allowed: [
        'formula',
        'qrcode_value_field_id',
        'barcode_value_field_id',
        'relation_type',
        'related_table_id',
        'related_field_id',
        'related_table_rollup_field_id',
        'related_table_lookup_field_id',
        'rollup_function',
        'button_hook_id',
        'script_id',
        'webhook_id',
        'type',
        'prompt',
        'integration_id',
        'output_column_ids',
        'label',
        'color',
        'theme',
        'icon',
    ],
    mappings: {
        formula: 'formula_raw',
        qrcode_value_field_id: 'fk_qr_value_column_id',
        barcode_value_field_id: 'fk_barcode_value_column_id',
        relation_type: 'type',
        button_hook_id: 'fk_webhook_id',
        script_id: 'fk_script_id',
        webhook_id: 'fk_webhook_id',
        // parent id we need to extract from the url
        related_table_id: 'childId',
        related_field_id: 'fk_relation_column_id',
        related_table_rollup_field_id: 'fk_rollup_column_id',
        related_table_lookup_field_id: 'fk_lookup_column_id',
    },
    transformFn: (data) => {
        // Handle button specific transformations
        if (data.type) {
            // For button columns, convert type-specific structure to the V2 format
            if (data.type === 'formula' ||
                data.type === 'webhook' ||
                data.type === 'script' ||
                data.type === 'ai') {
                return Object.assign(Object.assign({}, data), { type: data.type });
            }
        }
        return data;
    },
});
export const columnV3ToV2Builder = builderGenerator({
    allowed: [
        'id',
        'title',
        'type',
        'default_value',
        'options',
        'description',
        'unique',
    ],
    mappings: {
        type: 'uidt',
        default_value: 'cdf',
        options: 'meta',
    },
    meta: {
        snakeCase: false,
        camelCase: true,
        metaProps: ['options', 'meta'],
        mappings: {
            '12hr_format': 'is12hrFormat',
            // legacy V3 field — preserved so older clients sending locale_string
            // still resolve via resolveColumnSeparator on read
            locale_string: 'isLocaleString',
            separator: 'separator',
            rich_text: 'richMode',
            display_timezone: 'isDisplayTimezone',
            // LongText
            generate_text_using_ai: LongTextAiMetaProp,
            allow_multiple_users: 'is_multi',
            show_as_progress: 'is_progress',
            max_value: 'max',
            // duration_format: 'duration',
        },
        skipfn: (data) => columnsWithOptions.includes(data.uidt || data.type),
        excluded: ['defaultViewColOrder', 'singular', 'plural'],
        skipTransformFor: [
            'currency_locale',
            'currency_code',
            'icon',
            'iconIdx',
            'duration_format',
            'is_multi',
            'is_progress',
            'max',
        ],
    },
    transformFn: (data) => {
        var _a, _b;
        const meta = data.meta || {};
        let colOptions;
        switch (data.uidt) {
            case UITypes.SingleSelect:
            case UITypes.MultiSelect:
                {
                    // when choices not sent, do not override
                    if (meta.choices) {
                        const choices = (_b = (_a = meta.choices) === null || _a === void 0 ? void 0 : _a.map((opt) => {
                            const res = {
                                title: opt.title,
                                color: opt.color,
                            };
                            if (opt.id)
                                res.id = opt.id;
                            return res;
                        })) !== null && _b !== void 0 ? _b : [];
                        colOptions = { options: choices };
                    }
                }
                break;
            default:
                // todo: handle LTAR/Lookup/Rollup
                break;
        }
        if (data.uidt === UITypes.Checkbox) {
            const { icon } = meta;
            if (icon) {
                const iconIdx = checkboxIconList.findIndex((ic) => ic.label === icon);
                if (iconIdx !== -1) {
                    const _c = checkboxIconList[iconIdx], { label: _label } = _c, rest = __rest(_c, ["label"]);
                    meta.iconIdx = iconIdx;
                    meta.icon = rest;
                }
            }
        }
        else if (data.uidt === UITypes.Rating) {
            const { icon } = meta;
            if (icon) {
                const iconIdx = ratingIconList.findIndex((ic) => ic.label === icon);
                if (iconIdx !== -1) {
                    const _d = ratingIconList[iconIdx], { label: _label } = _d, rest = __rest(_d, ["label"]);
                    meta.iconIdx = iconIdx;
                    meta.icon = rest;
                }
            }
        }
        else if (data.uidt === UITypes.Duration) {
            const { duration, duration_format } = meta;
            const durationFormat = duration !== null && duration !== void 0 ? duration : duration_format;
            // extract option meta and include only label and color
            const durationIdx = durationOptions.findIndex((d) => d.title === durationFormat);
            if (durationIdx > -1) {
                meta.duration = durationIdx;
            }
        }
        // if multi select then accept array of default values
        else if (data.uidt === UITypes.MultiSelect) {
            data.cdf = Array.isArray(data.cdf) ? data.cdf.join(',') : data.cdf;
        }
        let additionalPayloadData = {};
        if (columnsWithOptions.includes(data.uidt) && data.meta) {
            additionalPayloadData =
                columnOptionsV3ToV2Builder().build(data.meta) || {};
        }
        return Object.assign(Object.assign(Object.assign({}, data), { colOptions, meta: meta || data.meta }), additionalPayloadData);
    },
});
export const sortBuilder = builderGenerator({
    allowed: ['id', 'fk_column_id', 'direction'],
    mappings: {
        fk_column_id: 'field_id',
    },
});
export const filterBuilder = builderGenerator({
    allowed: [
        'id',
        'fk_column_id',
        'direction',
        'logical_op',
        'fk_parent_id',
        'comparison_op',
        'comparison_sub_op',
        'value',
        'is_group',
        'fk_related_col_id',
        'fk_value_col_id',
    ],
    mappings: {
        fk_column_id: 'field_id',
        fk_parent_id: 'parent_id',
        fk_value_col_id: 'value_field_id',
        comparison_op: 'operator',
        comparison_sub_op: 'sub_operator',
    },
    excludeNullProps: true,
    booleanProps: ['is_group'],
});
export const filterRevBuilder = builderGenerator({
    allowed: [
        'id',
        'field_id',
        'parent_id',
        'direction',
        'logical_op',
        'operator',
        'sub_operator',
        'value',
        'is_group',
    ],
    mappings: {
        field_id: 'fk_column_id',
        parent_id: 'fk_parent_id',
        operator: 'comparison_op',
        sub_operator: 'comparison_sub_op',
    },
});
export const viewColumnBuilder = builderGenerator({
    allowed: [
        'fk_column_id',
        'width',
        'show',
        'formatting',
        'label',
        'help',
        'description',
        'required',
        'aggregation',
    ],
    mappings: {
        fk_column_id: 'field_id',
    },
    excludeNullProps: true,
    booleanProps: ['show', 'required'],
    transformFn: (formattedData) => {
        if (formattedData.width === VIEW_GRID_DEFAULT_WIDTH + 'px') {
            formattedData.width = undefined;
        }
        else if (formattedData.width &&
            typeof formattedData.width === 'string' &&
            formattedData.width !== '') {
            const numberStrWidth = formattedData.width.replace('px', '').trim();
            const numberWidth = Number(numberStrWidth);
            formattedData.width = !isNaN(numberWidth) ? numberWidth : numberStrWidth;
        }
        if (formattedData.aggregation === 'none') {
            delete formattedData.aggregation;
        }
        return formattedData;
    },
});
//# sourceMappingURL=data:application/json;base64,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